<template>
  <div
    class="@container relative max-w-full overflow-hidden mb-5 bg-white rounded shadow-[0_2px_2px_0_rgba(0,0,0,0.04)] hover:shadow-[0_4px_8px_0_rgba(0,0,0,0.10)] focus-within:border focus-within:border-solid focus-within:border-primary-900"
    data-test="product-tile-wrapper"
    tabindex="0"
  >
    <CampaignBadges class="right-auto left-0 !items-start" badge-class="rounded-bl-none rounded-br" :badges="badges" />

    <ProductTile
      :class="{ 'product-tile__margin': badges.length > 0 }"
      :product="{ ...product, commonVariantAttributes: filteredAttributes as ProductAttribute[] }"
      :expanded="isVariantListExpanded"
      :link-to-pdp="variantLink"
      :image-size="120"
      :use-product-image="product?.variants?.length === product?.totalVariants"
      @toggle-product="toggleVariants"
      @navigation-click="trackNavigation"
    >
      <CLink v-if="configuratorRoute" :to="configuratorRoute" class="!font-semibold flex justify-center">
        {{ t('go_to_product_configurator') }}
      </CLink>
    </ProductTile>
    <keep-alive>
      <LazyVariantTable
        v-if="isVariantListExpanded && !isSmallScreen"
        :product="mutableProduct"
        :layout="'pdp'"
        @variant-table-updated="onVariantTableUpdated"
      />
    </keep-alive>
    <LazyVariantsFlyout
      v-if="isVariantListExpanded && isSmallScreen"
      :product-name="product.name"
      :articles-number="product.totalVariants"
      @close="toggleVariants"
    >
      <template #table>
        <LazyVariantTable :product="mutableProduct" :layout="'plp'" @variant-table-updated="onVariantTableUpdated" />
      </template>
    </LazyVariantsFlyout>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import gql from '@/gql/variant-table';
import { useDeliveryStore, useUserStore, useProductStore, useTrackingStore, useBrandStore } from '@/stores';
import type { CampaignBadge } from '@/types/campaign';
import type { Product, Variant, ProductAttribute } from '@/types/products';
import { SIZE_DESCRIPTION_ATTRIBUTE_NAME } from '~/utils/Products';

type Props = {
  product: Product & { totalVariants?: number };
  badges?: CampaignBadge[];
  isVariantsToggled?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  badges: () => [],
  isVariantsToggled: false,
});

const emit = defineEmits<{
  (e: 'variants-toggled', v: boolean): void;
}>();

const { mq } = useMediaQueries();
const { selectedLocations, getCurrentStockLocations, getSelectedDeliveryId, getStockInput } = storeToRefs(
  useDeliveryStore(),
);
const { isLoggedIn } = storeToRefs(useUserStore());
const { productHistory } = storeToRefs(useProductStore());
const { pushProductHistory } = useProductStore();
const { visitProduct } = useTrackingStore();
const attributesToHide: any[] = inject('attributesToHide', []);
const router = useRouter();
const { t } = useI18n();
const { $apiGateway, $sentry, $tracking } = useNuxtApp();
const config = useRuntimeConfig();
const { brandConfig } = useBrandStore();
const { getPreferredStore } = storeToRefs(useUserStore());

const isVariantListExpanded = ref(false);
const mutableProduct = ref({ ...props.product });
const stockIsLoading = ref(false);
const priceIsLoading = ref(false);
const hasFetchedPriceAndStock = ref(false);
const variantSlug = ref<string | undefined>(undefined);

provide('isLoading', ref({ stock: stockIsLoading, price: priceIsLoading }));

const isSmallScreen = computed(() => mq.value.xsmall || mq.value.small || mq.value.medium);

const variantLink = computed(() => linkToPdp(variantSlug.value || props.product?.variants?.[0]?.variantSlug || ''));
const filteredAttributes = computed(() => {
  const filteredArray = props.product?.commonVariantAttributes;

  if (
    brandConfig.additions.displaySizeDescriptionAsSecondAttribute &&
    !!props.product?.commonVariantAttributes &&
    !!filteredArray
  ) {
    const sizeDescriptionAttributeIndex = props.product.commonVariantAttributes?.findIndex(
      (attribute) => attribute.attributeName === SIZE_DESCRIPTION_ATTRIBUTE_NAME,
    );

    if (sizeDescriptionAttributeIndex >= 0) {
      filteredArray.splice(1, 0, filteredArray.splice(sizeDescriptionAttributeIndex, 1)[0]);
    }
  }

  return filteredArray?.filter(({ attributeName }) => !attributesToHide.includes(attributeName));
});

const configuratorRoute = computed(() => {
  if (!props.product || !brandConfig.additions.productConfiguration) return;
  const { variants } = props.product;
  if (!variants || !variants.length || !variants[0].configurators?.length) return;
  return {
    name: 'konfigurator',
    query: { instance: variants[0].configurators[0].slug },
  };
});

const getPriceAndStock = async () => {
  if (process.server || !isLoggedIn.value || !isVariantListExpanded.value) return;
  try {
    priceIsLoading.value = true;
    stockIsLoading.value = true;

    const articleNumbers = props.product?.variants?.map(({ articleNumber }) => articleNumber);
    const { data, error } = (await $apiGateway.post('/graphql', {
      query: gql.getPriceAndStock,
      variables: {
        articleNumbers,
        deliveryId: getSelectedDeliveryId.value,
        stockInput: getStockInput.value,
        locationId: getPreferredStore.value.id,
      },
    })) as any;
    if (error) throw error;
    const gqlVariants = data?.data?.variants;
    if (!gqlVariants) return;

    const updatedVariants = [];
    for (const variant of props.product?.variants || []) {
      const { articleNumber } = variant;
      const gqlVariant = gqlVariants.find((variant: Variant) => variant.articleNumber === articleNumber);
      const updatedVariant = {
        ...variant,
        ...gqlVariant,
      };
      updatedVariants.push(updatedVariant);
    }
    mutableProduct.value.variants = updatedVariants;

    hasFetchedPriceAndStock.value = true;
  } catch (error) {
    $sentry.captureException(error);
  } finally {
    priceIsLoading.value = false;
    stockIsLoading.value = false;
  }
};

watch(isLoggedIn, () => {
  getPriceAndStock();
});

watch(selectedLocations, (newValue, oldValue) => {
  if (newValue[getSelectedDeliveryId.value] !== oldValue[getSelectedDeliveryId.value]) {
    hasFetchedPriceAndStock.value = false;
    if (isVariantListExpanded.value) {
      getPriceAndStock();
    }
  }
});

watch(getCurrentStockLocations, () => {
  if (isVariantListExpanded.value) {
    hasFetchedPriceAndStock.value = false;
    getPriceAndStock();
  }
});

watch(
  () => props.isVariantsToggled,
  (to) => {
    isVariantListExpanded.value = to;
  },
);

const toggleVariants = () => {
  isVariantListExpanded.value = !isVariantListExpanded.value;
  if (isVariantListExpanded.value && !hasFetchedPriceAndStock.value) {
    getPriceAndStock();
  }
  emit('variants-toggled', isVariantListExpanded.value);
};

onMounted(() => {
  const { variant, productId } = productHistory.value;
  if (variant && productId === props.product.id) {
    toggleVariants();
    pushProductHistory({
      variant: null,
    });
  }
});

const onVariantTableUpdated = (variants: Variant[]) => {
  variantSlug.value = variants[0]?.variantSlug;
};

const trackNavigation = () => {
  $tracking.emit('productClick', {
    product: props.product,
    pageType: router.currentRoute.value.name,
    list: config.public.gtm.PRODUCT_LIST,
  });
  visitProduct(props.product.variants?.[0]?.articleNumber || '');
  pushProductHistory({
    productId: props.product.id,
  });
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductTileWrapper',
});
</script>
<style lang="scss" scoped>
:deep(.product-tile__heading-link) {
  @apply text-primary-900 no-underline;
}
</style>
